import type { Schema } from '@cli-types/core-engine/schema.js'
import { flattenFields } from '@core-engine/schema/field-helpers/flattening/flatten-fields.js'
import { collectInvalidColumnTypeErrors } from './collect-invalid-column-type-errors.js'
import { collectInvalidDefaultValueFromErrors } from './collect-invalid-default-value-from-errors.js'
import { collectInvalidEntitySlugErrors } from './collect-invalid-entity-slug-errors.js'
import { collectInvalidHeightErrors } from './collect-invalid-height-errors.js'
import { collectInvalidSchemaFieldTypeErrors } from './collect-invalid-schema-field-type-errors.js'
import { collectInvalidSlotErrors } from './collect-invalid-slot-errors.js'
import { collectMissingFieldPropertyErrors } from './collect-missing-field-property-errors.js'
import { collectMissingGeneratedSchemaTitleErrors } from './collect-missing-generated-schema-title-errors.js'
import { collectRequiredSchemaMetadataErrors } from './collect-required-schema-metadata-errors.js'
import { collectReservedAuthorshipFieldErrors } from './collect-reserved-authorship-field-errors.js'
import { collectSchemaSlotLayoutErrors } from './collect-schema-slot-layout-errors.js'
import { normalizeEntitySchema } from './normalize-entity-schema.js'

export function validateEntitySchema(schema: Schema): string[] {
  const errors: string[] = []
  collectSchemaSlotLayoutErrors(schema, errors)
  const normalizedSchema = normalizeEntitySchema(schema)

  collectRequiredSchemaMetadataErrors(normalizedSchema, errors)
  if (normalizedSchema.type !== 'entity') {
    errors.push('Entity schema must have `type: "entity"`')
  }
  if (!Array.isArray(normalizedSchema.columns) || normalizedSchema.columns.length === 0) {
    errors.push('Schema must have at least one column')
  }

  collectMissingFieldPropertyErrors(normalizedSchema, errors)

  collectInvalidSchemaFieldTypeErrors(normalizedSchema.fields || [], errors)
  collectInvalidSlotErrors(normalizedSchema.fields || [], '', errors)
  collectInvalidHeightErrors(normalizedSchema.fields || [], '', errors)
  collectMissingGeneratedSchemaTitleErrors(normalizedSchema, 'Entity', errors)
  collectInvalidDefaultValueFromErrors(normalizedSchema, errors)
  collectReservedAuthorshipFieldErrors(normalizedSchema, 'entity', errors)
  collectInvalidEntitySlugErrors(normalizedSchema, errors)
  collectInvalidColumnTypeErrors(normalizedSchema, errors)

  const flatFields = flattenFields(normalizedSchema.fields || [])
  const fieldNames = new Set(flatFields.map((f) => f.name))
  for (const auto of [
    'id',
    'createdAt',
    'updatedAt',
    'published',
    'submittedAt',
    'ipAddress',
    'userAgent',
    'createdBy',
    'updatedBy'
  ]) {
    fieldNames.add(auto)
  }

  for (const column of normalizedSchema.columns || []) {
    if (!column.accessorKey || !column.header || !column.type) {
      errors.push(`Column is missing required properties: ${JSON.stringify(column)}`)
    } else if (!fieldNames.has(column.accessorKey)) {
      errors.push(
        `Column accessorKey "${column.accessorKey}" does not match any field. Available: ${Array.from(fieldNames).join(', ')}`
      )
    }
  }

  return errors
}
